
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { useBaseMfaState } from './useBaseMfaState';
import { useMfaFactors } from './useMfaFactors';
import { handleMfaError } from '../types';

/**
 * Hook for MFA challenge operations
 * 
 * Provides functionality to initiate MFA challenges during the authentication flow
 * when a user has MFA enabled on their account.
 * 
 * @returns MFA challenge functions
 */
export function useMfaChallenge() {
  const { 
    setIsLoading, 
    setIsMfaRequired, 
    setFactorId, 
    setChallengeId 
  } = useBaseMfaState();
  
  const { fetchMfaFactors } = useMfaFactors();

  /**
   * Initiate an MFA challenge for authentication
   * 
   * This function:
   * 1. Fetches the user's enrolled MFA factors
   * 2. If factors exist, initiates a challenge for the first verified factor
   * 3. Updates MFA state to indicate MFA is required
   * 4. Stores the factor ID and challenge ID for verification
   * 
   * @returns Promise resolving to boolean indicating if MFA is required
   */
  const initiateMfaChallenge = async (): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      // Check if MFA is required by fetching verified factors
      const factors = await fetchMfaFactors();
      
      if (factors.length > 0) {
        // User has verified MFA factors
        setIsMfaRequired(true);
        
        // Start the challenge with the first verified factor
        const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ 
          factorId: factors[0].id 
        });
        
        if (challengeError) throw challengeError;
        
        setFactorId(factors[0].id);
        setChallengeId(challenge?.id || null);
        return true;
      }
      
      return false;
    } catch (error) {
      const mfaError = handleMfaError(error);
      console.error('MFA challenge initiation failed:', mfaError);
      toast({
        title: "MFA Error",
        description: mfaError.message || "Failed to initiate MFA challenge",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    initiateMfaChallenge
  };
}
